import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Minus, Zap } from "lucide-react";

const STRATS = [
  {
    name: "Bullish",
    tag: "When you think it's going up",
    icon: TrendingUp,
    accent: "#10b981",
    legs: 3,
    winrate: "68%",
    chart: [10, 14, 12, 18, 22, 20, 26, 30, 28, 34],
  },
  {
    name: "Bearish",
    tag: "When you think it's going down",
    icon: TrendingDown,
    accent: "#ef4444",
    legs: 3,
    winrate: "64%",
    chart: [34, 30, 32, 26, 22, 24, 18, 14, 16, 10],
  },
  {
    name: "Sideways",
    tag: "When it's going nowhere",
    icon: Minus,
    accent: "#084392",
    legs: 4,
    winrate: "72%",
    chart: [20, 22, 19, 21, 23, 20, 22, 19, 21, 20],
  },
  {
    name: "Momentum",
    tag: "When it's breaking out",
    icon: Zap,
    accent: "#f59e0b",
    legs: 2,
    winrate: "59%",
    chart: [12, 13, 14, 16, 22, 29, 31, 32, 34, 36],
  },
];

export default function Strategies() {
  return (
    <section
      id="strategies"
      data-testid="strategies-section"
      className="py-20 md:py-28 bg-[#f8fafc] border-y border-[#e2e8f0]"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="font-mono text-xs font-bold text-[#084392] tracking-[0.2em]">
            / PRE-BUILT STRATEGIES
          </div>
          <h2 className="font-heading mt-3 text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter">
            A strategy for every
            <br />
            <span className="text-[#084392]">mood of the market.</span>
          </h2>
          <p className="mt-5 text-lg text-[#475569] max-w-2xl leading-relaxed">
            Institution-grade templates, 100% customizable. Tweak leg
            configuration, timing, targets and SL in rupees — then save as your
            own.
          </p>
        </motion.div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {STRATS.map((s, i) => {
            const Icon = s.icon;
            const max = Math.max(...s.chart);
            const min = Math.min(...s.chart);
            const range = Math.max(1, max - min);
            const points = s.chart
              .map((v, idx) => {
                const x = (idx / (s.chart.length - 1)) * 200;
                const y = 60 - ((v - min) / range) * 50;
                return `${x},${y}`;
              })
              .join(" ");

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`strategy-card-${s.name.toLowerCase()}`}
                className="group relative rounded-2xl border border-[#e2e8f0] bg-white p-6 hover:border-[#084392]/50 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div
                    className="h-11 w-11 rounded-lg flex items-center justify-center"
                    style={{ background: `${s.accent}15`, color: s.accent }}
                  >
                    <Icon size={20} />
                  </div>
                  <span className="font-mono text-[10px] font-bold text-[#475569] bg-[#f8fafc] border border-[#e2e8f0] px-2 py-1 rounded">
                    {s.legs} LEGS
                  </span>
                </div>
                <h3 className="font-heading mt-5 text-2xl font-bold tracking-tight">
                  {s.name}
                </h3>
                <p className="mt-1 text-sm text-[#475569]">{s.tag}</p>

                <div className="mt-5 h-16 bg-[#f8fafc] rounded-lg p-2 relative overflow-hidden">
                  <svg viewBox="0 0 200 60" className="w-full h-full">
                    <polyline
                      points={points}
                      fill="none"
                      stroke={s.accent}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-[#475569]">Historical win-rate</span>
                  <span className="font-mono font-bold text-[#0f172a]">
                    {s.winrate}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-6 text-xs text-[#94a3b8] font-mono">
          * Historical performance is not a guarantee of future returns. Trade
          with capital you can afford to lose.
        </p>
      </div>
    </section>
  );
}
